import { useState } from "react";
import { useT } from "../../i18n";
import { addProduct } from "../../lib/shopApi";
import { toast } from "../../components/ui/sonner";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";

export const AddProductForm = ({ onAdded }) => {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ name_fr: "", name_en: "", desc_fr: "", image: "", sizes: "", category: "specialties" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const sizes = form.sizes
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const clean = line.replace(/CHF/gi, "").replace(/\.-+\s*$/, "").trim();
        const priceMatch = clean.match(/([\d]+(?:[.,]\d+)?)\s*$/);
        const price = priceMatch ? parseFloat(priceMatch[1].replace(",", ".")) : NaN;
        const label = priceMatch ? clean.slice(0, priceMatch.index).trim() : clean;
        return { label, price };
      })
      .filter((s) => s.label && !isNaN(s.price));
    if (!sizes.length) {
      toast.error(t.sh_sizes);
      return;
    }
    setSending(true);
    try {
      await addProduct({ name_fr: form.name_fr, name_en: form.name_en || form.name_fr, desc_fr: form.desc_fr, desc_en: form.desc_fr, image: form.image, sizes, category: form.category });
      toast.success(t.sh_product_added);
      setForm({ name_fr: "", name_en: "", desc_fr: "", image: "", sizes: "", category: "specialties" });
      onAdded();
    } catch {
      toast.error("Error");
    } finally {
      setSending(false);
    }
  };

  const inputCls = "bg-white/80 border-bergamote-600/25 focus-visible:ring-bergamote-500 text-bergamote-900";

  if (!open) {
    return (
      <button
        data-testid="owner-add-toggle"
        onClick={() => setOpen(true)}
        className="mx-auto block font-mono text-xs uppercase tracking-[0.25em] text-bergamote-500 underline underline-offset-4 hover:text-bergamote-700 transition-colors"
      >
        {t.sh_owner}
      </button>
    );
  }

  return (
    <form onSubmit={submit} className="bg-bergamote-50 border border-dashed border-bergamote-600/40 rounded-3xl p-6 sm:p-8 space-y-4" data-testid="owner-add-form">
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-bergamote-600">{t.sh_owner}</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label className="text-bergamote-700 text-xs">{t.sh_name_fr}</Label>
          <Input required value={form.name_fr} onChange={set("name_fr")} className={inputCls} data-testid="owner-name-fr" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-bergamote-700 text-xs">{t.sh_name_en}</Label>
          <Input value={form.name_en} onChange={set("name_en")} className={inputCls} data-testid="owner-name-en" />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label className="text-bergamote-700 text-xs">{t.sh_desc}</Label>
        <Input value={form.desc_fr} onChange={set("desc_fr")} className={inputCls} data-testid="owner-desc" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-bergamote-700 text-xs">{t.sh_sizes}</Label>
        <Textarea required rows={2} placeholder={"250 g 14\n500 g 26"} value={form.sizes} onChange={set("sizes")} className={inputCls} data-testid="owner-sizes" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-bergamote-700 text-xs">{t.sh_image}</Label>
        <Input value={form.image} onChange={set("image")} className={inputCls} placeholder="https://…" data-testid="owner-image" />
      </div>
      <div className="space-y-1.5">
        <Label className="text-bergamote-700 text-xs">{t.sh_category}</Label>
        <div className="flex gap-2" data-testid="owner-category">
          {["specialties", "tea"].map((c) => (
            <button
              key={c}
              type="button"
              data-testid={`owner-category-${c}`}
              onClick={() => setForm((f) => ({ ...f, category: c }))}
              className={`rounded-full px-4 py-2 text-xs font-semibold border transition-colors ${
                form.category === c
                  ? "bg-bergamote-600 text-cream-50 border-bergamote-600"
                  : "bg-white/80 text-bergamote-700 border-bergamote-600/30 hover:border-bergamote-600"
              }`}
            >
              {c === "tea" ? t.sh_cat_tea : t.sh_cat_specialties}
            </button>
          ))}
        </div>
      </div>
      <button
        type="submit"
        disabled={sending}
        data-testid="owner-add-submit"
        className="rounded-full bg-bergamote-600 text-cream-50 px-7 py-3 text-sm font-semibold hover:bg-bergamote-700 disabled:opacity-60 transition-colors"
      >
        {sending ? "…" : t.sh_add_product}
      </button>
    </form>
  );
};
